import * as React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useParams, useSearchParams } from "react-router-dom";

import { dayjs } from "@/lib/dayjs";
import { LeadUpdateAction, useAnalyseCampaignLead, useUpdateCampaignLead } from "@/hooks/api/use-campaigns";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { CustomTimePicker } from "@/components/customTimePicker/custom-time-picker";
import { FILTER_SEPARATOR } from "@/constants";

export interface UpdateLeadModalProps {
	open: boolean;
	onClose: () => void;
	selectedLeads: {
		id?: string;
		rows: number[];
		isAllDataSelected: boolean;
		action: string;
		label: string;
	};
}

export function UpdateLeadModal({ open, onClose, selectedLeads }: UpdateLeadModalProps): React.JSX.Element {
	const [searchParams] = useSearchParams();
	const { campaignId } = useParams();
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [scheduleAt, setScheduleAt] = React.useState<string | null>(null);
	const [scheduleAtError, setScheduleAtError] = React.useState<boolean>(false);

	const updateCampaignLeadMutation = useUpdateCampaignLead();
	const analyseCampaignLeadMutation = useAnalyseCampaignLead();

	const handleAction = async (rows: number[], isAllDataSelected: boolean, action: string) => {
		const data: any = {};

		const dispositionParam = searchParams.getAll("disposition");
		const statusParam = searchParams.get("status");

		const splitDispositionIds = dispositionParam?.map((d) => d.split(FILTER_SEPARATOR));
		const splitStatus = statusParam?.split(FILTER_SEPARATOR);

		const dispositions = splitDispositionIds?.map((d) => d.at(-1) ?? "");
		const status = splitStatus?.at(-1);
    
    const attemptsFrom = searchParams.get("attemptsFrom") ?? '';
    const attemptsTo = searchParams.get("attemptsTo") ?? '';

		if (action === LeadUpdateAction.ADD_TO_QUEUE) {
			data.status = "pending";
		}
		if (action === LeadUpdateAction.SCHEDULE_CUSTOM_CALL_BACK) {
			data.status = "scheduled";
			data.scheduleAt = scheduleAt ? dayjs(Number(scheduleAt)).format("YYYY-MM-DD HH:mm:ss") : null;
		}
		if (action === LeadUpdateAction.RESET_CALL_BACK_ATTEMPTS) {
			data.resetAttempts = true;
		}

		// if (action === LeadUpdateAction.ANALYSE_CALL) {
		// }

		data.ids = isAllDataSelected ? undefined : rows;

		try {
			await (action === LeadUpdateAction.ANALYSE
				? analyseCampaignLeadMutation.mutateAsync({
						campaignId: campaignId!,
						data,
						disposition: dispositions,
            attemptsFrom: attemptsFrom,
            attemptsTo: attemptsTo,
						status,
						id: selectedLeads?.id,
						action,
					})
				: updateCampaignLeadMutation.mutateAsync({
						campaignId: campaignId!,
						data,
						disposition: dispositions,
            attemptsFrom: attemptsFrom,
            attemptsTo: attemptsTo,
						status,
						id: selectedLeads?.id,
						action,
					}));
		} catch (error) {
			console.error(error);
		}
	};

	const handleClose = () => {
		onClose();
		setTimeout(() => {
			setScheduleAt(null);
			setIsSubmitting(false);
			setScheduleAtError(false);
		}, 300);
	};

	const handleSumbit = async () => {
		setIsSubmitting(true);

		if (selectedLeads?.action === LeadUpdateAction.SCHEDULE_CUSTOM_CALL_BACK && !scheduleAt) {
			setScheduleAtError(true);
			setIsSubmitting(false);
			return;
		} else {
			setScheduleAtError(false);
		}

		try {
			await handleAction(selectedLeads?.rows, selectedLeads?.isAllDataSelected, selectedLeads?.action);
		} catch (error) {
			console.log(error);
		} finally {
			handleClose();
		}
	};

	if (!selectedLeads) return <></>;

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogContent sx={{ p: 3, overflow: "visible" }}>
				<Stack direction="row" spacing={2} sx={{ display: "flex" }}>
					<Stack spacing={3}>
						<Stack spacing={1}>
							<Typography variant="h5">{`Confirm "${selectedLeads.label}"?`}</Typography>
							<Typography color="text.secondary" variant="body2">
								{`Are you sure you want to do this action to the selected lead${selectedLeads.isAllDataSelected || selectedLeads.rows.length > 1 ? "s" : ""}?`}
							</Typography>
							{selectedLeads.action === LeadUpdateAction.DO_NOT_CALL_AGAIN ? (
								<Alert severity="warning" sx={{ mt: 1 }}>
									This is <strong>account-wide</strong>, not just this campaign. These contacts will not be dialled by
									any campaign, now or in future, and any leads already waiting for them are cancelled. You can lift it
									later from Settings &rarr; Do Not Call.
								</Alert>
							) : null}
						</Stack>
					</Stack>
				</Stack>
				{selectedLeads.action === LeadUpdateAction.SCHEDULE_CUSTOM_CALL_BACK && (
					<Stack sx={{ mt: 2 }}>
						<CustomTimePicker
							label="Schedule Custom Call Back At"
							required
							value={scheduleAt}
							setValue={(value) => {
								setScheduleAt(value ? value.toString() : null);
								setScheduleAtError(false);
							}}
							centered
							error={scheduleAtError}
						/>
						{scheduleAtError && (
							<CustomFormHelperError
								text="Schedule Custom Call Back At is required"
								color="var(--mui-palette-error-main)"
							/>
						)}
					</Stack>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 3, pt: 0 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={handleClose}>
						Cancel
					</Button>
					<Button color="primary" variant="contained" onClick={handleSumbit} disabled={isSubmitting}>
						Confirm
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
